import { Component, inject } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { VendorEffects } from "../../State/VendorEffects";
import { VendorType } from "../../Dto/VendorModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { CreateVendorRequest } from "../../Dto/CreateVendorRequest";

@Component({
  selector: "app-vendor-create",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    LocationSelectorComponent,
    RichTextEditorComponent,
    AutocompleteComponent,
  ],
  templateUrl: "./VendorCreatePage.html",
  styleUrl: "./VendorCreatePage.scss",
})
export class VendorCreatePage {
  private readonly fb = inject(FormBuilder);
  private readonly vendorEffects = inject(VendorEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly router = inject(Router);

  readonly typeOptions = Object.values(VendorType).map((type) => ({
    value: type,
    label: type,
  }));

  isSaving = false;
  form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      name: ["", [Validators.required]],
      type: ["", [Validators.required]],
      contactPerson: [""],
      phone: [""],
      email: [""],
      note: [""],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        code: [""],
        longitude: [null],
        latitude: [null],
      }),
    });
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastEffects.show("Please fill in the required fields.", "error");
      return;
    }

    this.isSaving = true;
    const formData = this.form.value;
    const payload: CreateVendorRequest = {
      name: formData.name,
      type: formData.type,
      contactPerson: formData.contactPerson || undefined,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      address: {
        stateProvinceRegion: formData.address.stateProvinceRegion,
        countyDistrict: formData.address.countyDistrict,
        subdistrictMunicipality: formData.address.subdistrictMunicipality,
        communeVillage: formData.address.communeVillage,
        line: formData.address.line || "",
        zip: formData.address.zip || "",
        code: formData.address.code || "",
        longitude: formData.address.longitude ?? null,
        latitude: formData.address.latitude ?? null,
      },
      note: formData.note || undefined,
    };

    try {
      await this.vendorEffects.createVendor(payload);

      this.toastEffects.show("Vendor created.", "success");
      await this.router.navigate(["/vendors"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create vendor."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/vendors"]).catch(() => undefined);
  }

  onTypeChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }

    this.form.patchValue({ type: value.toString() });
  }

  onAddressChange(value: LocationAddress): void {
    this.form.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
        code: value.code,
        longitude: value.longitude,
        latitude: value.latitude,
      },
    });
  }
}
